import { useRef, useEffect, useCallback, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { useDrag } from "@use-gesture/react"
import { Capacitor } from "@capacitor/core"
import { useViewContext } from "@/app/stores/viewContext"
import { useAndroidBack } from "@/shared/hooks/useAndroidBack"
import { BACK_PRIORITY } from "@/shared/utils/platform/backHandlerStack"
import { sidebarDndActive } from "./SidebarDndContext"
import { sidebarSwipeActive } from "./SwipeableSidebarItem"
import { cn } from "@/shared/utils/general/cn"

/** Width of the touch zone on the left edge that starts a drawer open gesture */
const EDGE_SWIPE_ZONE = 20 // px
/** Velocity threshold — fast swipe completes regardless of distance */
const VELOCITY_THRESHOLD = 0.5

interface MobileDrawerProps {
  children: ReactNode
}

/**
 * Full-screen swipeable sidebar drawer for mobile.
 * Things 3 pattern: swipe from left edge to open, swipe right to close.
 *
 * Uses @use-gesture/react for native-feel gesture handling.
 */
export function MobileDrawer({ children }: MobileDrawerProps) {
  const { t } = useTranslation()
  const isOpen = useViewContext((s) => s.sidebarOpen)
  const setSidebarOpen = useViewContext((s) => s.setSidebarOpen)
  const drawerRef = useRef<HTMLDivElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const isGesturing = useRef(false)

  // Android back button → close sidebar
  const handleBack = useCallback(() => {
    setSidebarOpen(false)
    return true
  }, [setSidebarOpen])
  useAndroidBack(BACK_PRIORITY.SIDEBAR, isOpen, handleBack, "sidebar")

  // Track gesture translate X for direct manipulation during drag
  const translateX = useRef(0)
  // AbortController to cancel pending transitionend listeners (prevents race conditions)
  const closeAbort = useRef<AbortController | null>(null)

  // Show/hide drawer for Android WebView rendering (prevents bleed-through)
  function setDrawerVisible(visible: boolean) {
    if (drawerRef.current) {
      drawerRef.current.style.visibility = visible ? "visible" : "hidden"
    }
  }

  // Apply transform directly for 60fps (no state re-renders during drag)
  function applyTransform(x: number) {
    if (drawerRef.current) {
      drawerRef.current.style.transform = `translateX(${x}px)`
      drawerRef.current.style.transition = "none"
    }
    if (backdropRef.current) {
      const vw = drawerRef.current?.offsetWidth || window.innerWidth
      const progress = Math.max(0, (vw + x) / vw) // 0 when fully left, 1 when fully visible
      backdropRef.current.style.opacity = String(progress * 0.5)
      backdropRef.current.style.transition = "none"
    }
  }

  // Snap to open or closed with CSS transition
  function snapTo(open: boolean) {
    // Cancel any pending close transitionend that would hide the drawer
    if (closeAbort.current) {
      closeAbort.current.abort()
      closeAbort.current = null
    }

    if (open) {
      setDrawerVisible(true)
    }
    if (drawerRef.current) {
      drawerRef.current.style.transition =
        "transform 300ms cubic-bezier(0.22, 1.0, 0.36, 1)"
      drawerRef.current.style.transform = open
        ? "translateX(0)"
        : "translateX(-100%)"
      if (!open) {
        const abort = new AbortController()
        closeAbort.current = abort
        drawerRef.current.addEventListener(
          "transitionend",
          () => {
            if (!abort.signal.aborted) {
              setDrawerVisible(false)
            }
          },
          { once: true, signal: abort.signal },
        )
      }
    }
    if (backdropRef.current) {
      backdropRef.current.style.transition =
        "opacity 300ms cubic-bezier(0.22, 1.0, 0.36, 1)"
      backdropRef.current.style.opacity = open ? "0.5" : "0"
    }
  }

  // Sync with React state changes
  useEffect(() => {
    snapTo(isOpen)
  }, [isOpen])

  // Sync status bar style when drawer opens/closes.
  // When FocusBanner is active it forces Style.Dark (white icons on colored bg).
  // The drawer slides OVER the banner, exposing the light sidebar background,
  // so we temporarily force Style.Light (dark icons) while the drawer is open.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    let cancelled = false
    void (async () => {
      try {
        const { StatusBar, Style } = await import("@capacitor/status-bar")
        if (cancelled) return
        const isDark = document.documentElement.classList.contains("dark")
        if (isOpen) {
          // Drawer covers banner — use theme-appropriate style
          await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light })
        } else {
          // Drawer closed — check if FocusBanner should reclaim control
          const { isStatusBannerActive } = await import("@/app/components/AppStatusBanner")
          if (cancelled) return
          if (isStatusBannerActive()) {
            // Let banner control (it sets Dark)
            await StatusBar.setStyle({ style: Style.Dark })
          } else {
            await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light })
          }
        }
      } catch {
        // Plugin not available
      }
    })()

    return () => { cancelled = true }
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && isOpen) {
        setSidebarOpen(false)
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, setSidebarOpen])

  // Gesture on the drawer panel itself (for closing by swiping left)
  const bindDrawer = useDrag(
    ({ movement: [mx], velocity: [vx], direction: [dx], active }) => {
      // Block swipe-to-close while sidebar DnD or item swipe is active
      if (sidebarDndActive || sidebarSwipeActive) return

      // Only allow swiping left (to close)
      if (mx > 0) {
        applyTransform(0)
        return
      }

      if (active) {
        applyTransform(mx)
        translateX.current = mx
      } else {
        // Released — decide snap
        const shouldClose = mx < -100 || (vx > VELOCITY_THRESHOLD && dx < 0)
        if (shouldClose) {
          setSidebarOpen(false)
        } else {
          snapTo(true)
        }
        translateX.current = 0
      }
    },
    { axis: "x", filterTaps: true },
  )

  // Edge-swipe gesture on the content area (for opening)
  const bindEdge = useDrag(
    ({
      xy: [startX],
      movement: [mx],
      velocity: [vx],
      direction: [dx],
      active,
      first,
    }) => {
      // Only start from the left edge
      if (first && startX > EDGE_SWIPE_ZONE) {
        return
      }

      // Make drawer visible on first frame of gesture
      if (first) {
        isGesturing.current = true
        setDrawerVisible(true)
      }

      if (active) {
        // Map gesture to drawer position: starts at -drawerWidth, moves toward 0
        const vw = drawerRef.current?.offsetWidth || window.innerWidth
        const drawerX = Math.min(0, -vw + mx)
        applyTransform(drawerX)
      } else {
        isGesturing.current = false
        // Released — decide snap
        const shouldOpen = mx > 100 || (vx > VELOCITY_THRESHOLD && dx > 0)
        if (shouldOpen) {
          setSidebarOpen(true)
        } else {
          snapTo(false)
        }
      }
    },
    { axis: "x", filterTaps: true },
  )

  return (
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className={cn(
          "fixed inset-0 z-40 bg-black",
          isOpen ? "pointer-events-auto" : "pointer-events-none",
        )}
        style={{ opacity: 0 }}
        onClick={() => {
          if (!sidebarDndActive && !sidebarSwipeActive) setSidebarOpen(false)
        }}
        aria-hidden="true"
      />

      {/* Drawer panel — inline white fallback for Android WebView where
         oklch compositing can fail on fixed/transformed layers.
         The Sidebar child paints its themed bg-sidebar on top. */}
      <div
        ref={drawerRef}
        {...bindDrawer()}
        className="fixed inset-y-0 left-0 z-50 w-full max-w-[420px] shadow-xl"
        style={{
          transform: "translateX(-100%)",
          visibility: "hidden",
          backgroundColor: "#fff",
        }}
        role={isOpen ? "dialog" : undefined}
        aria-modal={isOpen ? "true" : undefined}
        aria-label={t("Sidebar navigation")}
      >
        {children}
      </div>

      {/* Invisible edge swipe zone — only render when drawer is closed */}
      {!isOpen && (
        <div
          {...bindEdge()}
          className="fixed inset-y-0 left-0 z-30"
          style={{ width: EDGE_SWIPE_ZONE }}
          aria-hidden="true"
        />
      )}
    </>
  )
}
